import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { User, AuthContextType, AccountType } from '../types/auth';
import { UserProfile } from '../types/subjects';
import { authAPI, profileAPI } from '../utils/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(
    localStorage.getItem('access_token')
  );
  const [isInitializing, setIsInitializing] = useState(true);

  // Initialize user and profile if token exists
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');

      if (token && refreshToken) {
        try {
          setAccessToken(token); // Set the token immediately
          const profile = await profileAPI.getProfile();
          setUserProfile(profile);
          setUser({
            id: profile.email,
            fullName: profile.full_name,
            email: profile.email,
            isAdmin: profile.is_admin,
            joinedYear: new Date().getFullYear(),
            profileImage: profile.profile_picture || undefined,
          });
          console.log('✅ Auth initialized successfully from stored tokens');
        } catch (error: any) {
          console.error('Failed to initialize auth with stored token:', error);

          // Only clear tokens if we get a 401 or explicit auth error
          // Don't clear on network errors or other transient issues
          const isAuthError = error?.message?.includes('401') ||
                             error?.message?.includes('Session expired') ||
                             error?.message?.includes('refresh token');

          if (isAuthError) {
            console.log('Auth error detected, clearing tokens');
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            setAccessToken(null);
            setUser(null);
            setUserProfile(null);
          } else {
            console.log('Non-auth error, keeping tokens for retry');
            // For non-auth errors, keep the tokens but don't set user
            // User will be redirected to login but can retry
            setUser(null);
            setUserProfile(null);
          }
        }
      } else if (token || refreshToken) {
        // If we have only one token, clear both for consistency
        console.log('Incomplete token set, clearing all tokens');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        setAccessToken(null);
      }
      setIsInitializing(false);
    };

    initializeAuth();
  }, []); // Empty dependency array to run only once on mount

  const login = async (email: string, password: string) => {
    try {
      const response = await authAPI.login({ email, password });
      
      // Store the access token (refresh token is automatically stored in authAPI.login)
      setAccessToken(response.access_token);
      
      // Fetch user profile after successful login
      try {
        const profile = await profileAPI.getProfile();
        setUserProfile(profile);
        
        // Set user with profile data
        setUser({
          id: profile.email, // Using email as ID for now
          fullName: profile.full_name,
          email: profile.email,
          isAdmin: profile.is_admin,
          joinedYear: new Date().getFullYear(),
          profileImage: profile.profile_picture || undefined,
        });
      } catch (profileError) {
        console.error('Failed to fetch profile after login:', profileError);
        // Still set a basic user object even if profile fetch fails
        setUser({
          id: 'temp',
          fullName: 'User',
          email: email,
          joinedYear: new Date().getFullYear(),
        });
      }
      setIsInitializing(false);
    } catch (error) {
      throw error;
    }
  };

  const signup = async (fullName: string, email: string, password: string, accountType: AccountType, parentalConsentGiven?: boolean) => {
    try {
      const response = await authAPI.register({
        email,
        password,
        full_name: fullName,
        account_type: accountType,
        parental_consent_given: parentalConsentGiven,
      });
      
      // Assuming the API returns user data in the response
      setUser({
        id: response.user?.id || response.id || '1',
        fullName: response.user?.full_name || response.full_name || fullName,
        email: response.user?.email || response.email || email,
        joinedYear: response.user?.joined_year || new Date().getFullYear(),
        profileImage: response.user?.profile_image,
      });
      setIsInitializing(false);
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Call the logout API to invalidate refresh token on server
      await authAPI.logout();
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // Always clear local state
      setUser(null);
      setUserProfile(null);
      setAccessToken(null);
    }
  };

  const updateUserProfile = (profile: UserProfile) => {
    setUserProfile(profile);
    if (user) {
      setUser({
        ...user,
        fullName: profile.full_name || user.fullName,
        email: profile.email || user.email,
      });
    }
  };

  const refreshProfile = useCallback(async () => {
    try {
      const profile = await profileAPI.getProfile();
      setUserProfile(profile);
      // Use functional update to avoid dependency on user
      setUser(currentUser => {
        if (currentUser) {
          return {
            ...currentUser,
            fullName: profile.full_name || currentUser.fullName,
            email: profile.email || currentUser.email,
            profileImage: profile.profile_picture || currentUser.profileImage,
          };
        }
        return currentUser;
      });
    } catch (error) {
      console.error('Failed to refresh profile:', error);
    }
  }, []); // Empty dependency array - function never recreated

  const initializeFromStoredTokens = async () => {
    const token = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');

    if (token && refreshToken) {
      try {
        setAccessToken(token);
        const profile = await profileAPI.getProfile();
        setUserProfile(profile);
        setUser({
          id: profile.email,
          fullName: profile.full_name,
          email: profile.email,
          joinedYear: new Date().getFullYear(),
        });
      } catch (error) {
        console.error('Failed to initialize from stored tokens:', error);
        throw error;
      }
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      userProfile,
      login,
      signup,
      logout,
      isAuthenticated: !!user,
      accessToken,
      updateUserProfile,
      refreshProfile,
      initializeFromStoredTokens,
      isInitializing
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
